import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Controller } from 'react-hook-form'
import { ExternalLink } from 'lucide-react'
import * as yup from 'yup'
import Button from '@/components/button'
import ChipSelect from '@/components/chip-select'
import ConfirmDialog from '@/components/confirm-dialog'
import DefinitionList from '@/components/definition-list'
import type { DefinitionItem } from '@/components/definition-list'
import FormField from '@/components/form-field'
import Select from '@/components/select'
import { useDefinirTiposArquivosDoTipoMutation } from '@/http/mutations/credenciais'
import { useTiposArquivosLookupQuery, useTiposCredenciaisLookupQuery, useTiposTokenLookupQuery } from '@/http/queries/lookups'
import type { Credencial } from '@/types/credencial'
import { useAuth } from '@/utility/context/auth-context'
import { ACAO } from '@/utility/permissions'
import { orDash } from '@/utility/Utils'
import CredencialForm from '../credencial-form'
import styles from '../style.module.css'

const schema = yup.object({
  tiposCredenciaisId: yup.string().required('Selecione o tipo de credencial'),
  tiposTokenIds: yup.array().of(yup.string().required()).required().default([]),
})

const OperadoraTipoTab = ({ credencial }: { credencial: Credencial }) => {
  const { can } = useAuth()
  const podeEditar = can(ACAO.EditarCredencial)
  const tipo = credencial.tiposCredenciais
  const operadoraId = tipo?.operadoras?.id ?? null

  const tiposCredenciaisQuery = useTiposCredenciaisLookupQuery(operadoraId)
  const tiposTokenQuery = useTiposTokenLookupQuery()
  const tiposArquivosQuery = useTiposArquivosLookupQuery()

  const [tiposArquivosSelecionados, setTiposArquivosSelecionados] = useState<string[]>(
    (tipo?.tipoArquivos ?? []).map((item) => String(item.id)),
  )
  const [confirmandoTiposArquivos, setConfirmandoTiposArquivos] = useState(false)
  const definirTiposArquivos = useDefinirTiposArquivosDoTipoMutation(
    credencial.tiposCredenciaisId,
    () => setConfirmandoTiposArquivos(false),
  )

  const opcoesTipoCredencial = (tiposCredenciaisQuery.data ?? []).map((item) => ({
    value: item.id,
    label: item.rotulo,
  }))
  const tipoAtualForaDaLista =
    credencial.tiposCredenciaisId &&
    !opcoesTipoCredencial.some((opcao) => opcao.value === credencial.tiposCredenciaisId)
  if (tipoAtualForaDaLista) {
    opcoesTipoCredencial.unshift({
      value: credencial.tiposCredenciaisId,
      label: tipo?.nomeAmigavel ?? 'Tipo atual',
    })
  }

  const operadoraItens: DefinitionItem[] = [
    {
      label: 'Operadora',
      value: orDash(tipo?.operadoras?.nomeFantasia ?? tipo?.operadoras?.razaoSocial),
    },
    { label: 'Registro ANS', value: orDash(tipo?.operadoras?.registroAns) },
    {
      label: 'Tipo de credencial',
      value: credencial.tiposCredenciaisId ? (
        <Link
          to={`/tipos-credenciais/${credencial.tiposCredenciaisId}`}
          className={styles.linkTipo}
          title="Abrir o tipo de credencial"
        >
          {orDash(tipo?.nomeAmigavel)}
          <ExternalLink size={13} aria-hidden />
        </Link>
      ) : (
        orDash(tipo?.nomeAmigavel)
      ),
    },
    { label: 'ID do tipo', value: orDash(credencial.tiposCredenciaisId) },
  ]

  return (
    <div className={styles.sections}>
      <DefinitionList items={operadoraItens} />

      <CredencialForm
        credencial={credencial}
        schema={schema}
        defaultValues={{
          tiposCredenciaisId: credencial.tiposCredenciaisId ?? '',
          tiposTokenIds: credencial.tiposToken.map((item) => item.id),
        }}
        montarPayload={(dados) => ({
          ...credencial,
          tiposCredenciaisId: dados.tiposCredenciaisId,
          tiposToken: dados.tiposTokenIds.map((id) => {
            const opcao = (tiposTokenQuery.data ?? []).find((item) => item.id === id)
            return { id, descricao: opcao?.rotulo ?? '' }
          }),
        })}
        mensagemConfirmacao="Trocar o tipo de credencial muda o crawler usado, os campos exigidos na autorização e o limite de procedimentos por guia, com efeito imediato. Deseja continuar?"
      >
        {({ control, formState: { errors } }, editar) => (
          <div className={styles.grid}>
            <FormField
              label="Tipo de credencial"
              htmlFor="tiposCredenciaisId"
              required
              error={errors.tiposCredenciaisId?.message}
            >
              <Controller
                control={control}
                name="tiposCredenciaisId"
                render={({ field }) => (
                  <Select
                    id="tiposCredenciaisId"
                    options={opcoesTipoCredencial}
                    isLoading={tiposCredenciaisQuery.isFetching}
                    disabled={!editar}
                    invalid={Boolean(errors.tiposCredenciaisId)}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </FormField>

            <FormField label="Tokens aceitos" htmlFor="tiposTokenIds">
              <Controller
                control={control}
                name="tiposTokenIds"
                render={({ field }) => (
                  <ChipSelect
                    ariaLabel="Tokens aceitos"
                    options={(tiposTokenQuery.data ?? []).map((item) => ({
                      value: item.id,
                      label: item.rotulo,
                    }))}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!editar}
                  />
                )}
              />
            </FormField>
          </div>
        )}
      </CredencialForm>

      <FormField
        label="Tipos de arquivo aceitos"
        htmlFor="tiposArquivosAceitos"
        hint="Vale para todas as credenciais que usam esse tipo, não só para essa."
      >
        <ChipSelect
          ariaLabel="Tipos de arquivo aceitos"
          options={(tiposArquivosQuery.data ?? []).map((item) => ({
            value: item.id,
            label: item.rotulo,
          }))}
          value={tiposArquivosSelecionados}
          onChange={(valor) => setTiposArquivosSelecionados(valor)}
          disabled={!podeEditar}
        />
      </FormField>

      {podeEditar ? (
        <div className={styles.actions}>
          <Button
            type="button"
            disabled={
              JSON.stringify([...tiposArquivosSelecionados].sort()) ===
              JSON.stringify((tipo?.tipoArquivos ?? []).map((item) => String(item.id)).sort())
            }
            onClick={() => setConfirmandoTiposArquivos(true)}
          >
            Salvar tipos de arquivo
          </Button>
        </div>
      ) : null}

      <ConfirmDialog
        isOpen={confirmandoTiposArquivos}
        toggle={() => setConfirmandoTiposArquivos(false)}
        title="Confirmar tipos de arquivo aceitos"
        message="Essa alteração vale para todas as credenciais que usam esse mesmo tipo, com efeito imediato. Deseja continuar?"
        confirmLabel="Salvar alterações"
        tone="warning"
        isLoading={definirTiposArquivos.isPending}
        onConfirm={() => definirTiposArquivos.mutate(tiposArquivosSelecionados)}
      />
    </div>
  )
}

export default OperadoraTipoTab
